import { useState, useRef, useCallback, useEffect } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import VitalsAPI from "src/vitals/VitalsAPI";
import PollVitalsHandler from "src/vitals/PollVitalsHandler";
import { AdminPermissions } from "src/common/Permissions";
import { EnvironmentType } from "src/common/Enums";

interface UseVitalsHandlerProps {
  currentUser: any;
  onPollComplete: (vitalsRunning: boolean) => void;
}

interface StartVitalsTestParams {
  datasetId: number;
  clientId: number;
  datasetLanguages: PickListItemResponseDTO[];
  newTestCustomFieldValues: CustomFieldValueDTO[];
  newTestPurposeId: number | undefined;
  selectedAssessmentResult: ClientSiteAssessmentResultsDTO | undefined;
}

interface ResumeVitalsTestParams {
  datasetId: number;
  datasetLanguages: PickListItemResponseDTO[];
  vitalsTestResultId: number | undefined;
}

export function useVitalsHandler({ currentUser, onPollComplete }: UseVitalsHandlerProps) {
  const [vitalsLaunched, setVitalsLaunchedState] = useState(false);
  const [DCATButtonClicked, setDCATButtonClicked] = useState(false);
  const [DCATRunning, setDCATRunning] = useState(false);
  const [DCATerror, setDCATerror] = useState<any>(undefined);
  const [canSync, setCanSync] = useState(false);
  const [syncSrc, setSyncSrc] = useState<string | undefined>(undefined);
  const [showLaunchMessageVisible, setShowLaunchMessageVisible] = useState(false);
  const [vitalsTestResultId, setVitalsTestResultId] = useState<number | undefined>(undefined);
  const [options] = useState<any>({ EvaluatorId: undefined, mode: "OFFLINE" });

  const showLaunchedMessageHandle = useRef<number>(-1);

  // Re-wire the poller callback when onPollComplete changes
  const onPollCompleteRef = useRef(onPollComplete);
  onPollCompleteRef.current = onPollComplete;

  const vitalsPoller = useRef<PollVitalsHandler>(
    new PollVitalsHandler((vitalsRunning: boolean) => onPollCompleteRef.current(vitalsRunning))
  );

  // Stop the vitals poller when the component unmounts
  useEffect(() => {
    return () => {
      vitalsPoller.current.Stop();
    };
  }, []);

  const setVitalsLaunched = useCallback(() => {
    setVitalsLaunchedState(true);
    showLaunchedMessageHandle.current = window.setTimeout(() => {
      setShowLaunchMessageVisible(true);
    }, 5000);
  }, []);

  const handleCustomProtocolError = useCallback(() => {
    console.log("Unable to launch VITALS");
  }, []);

  const startVitalsTest = useCallback(
    async ({
      datasetId,
      clientId,
      datasetLanguages,
      newTestCustomFieldValues,
      newTestPurposeId,
      selectedAssessmentResult,
    }: StartVitalsTestParams) => {
      if (
        !currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) &&
        !currentUser.vitalsCertified
      ) {
        toast.error("Not certified to start a VITALS test");
        return;
      }

      try {
        const vitalsTokenResponse = await VitalsAPI.vitalsGenerateToken(Number(datasetId));
        if (vitalsTokenResponse == null || vitalsTokenResponse.token == null) {
          throw new Error("unable to retrieve access token");
        }

        const newTest = {
          datasetId: Number(datasetId),
          clientId: Number(clientId),
          dateAdministered: moment().format("MMM DD, YYYY"),
          vitalsCustomFieldValues: [],
          assessmentCustomFieldValues: newTestCustomFieldValues,
          languageId: datasetLanguages[0].id,
          assessmentPurposeId: selectedAssessmentResult
            ? selectedAssessmentResult.assessmentPurposeId
            : newTestPurposeId,
        } as UpsertVitalsTestResultRequest;

        if (selectedAssessmentResult) {
          newTest.startFromAssessmentId = selectedAssessmentResult.id;
        }

        VitalsAPI.upsertVitalsTest(newTest).then((res) => {
          setVitalsTestResultId(res.id);
          VitalsAPI.vitalsGetPayload(res.id!).then((payload: any) => {
            setDCATButtonClicked(true);
            const data = {
              Environment: EnvironmentType.NextGen,
              VisitIdNG: payload.WebId,
              VisitId: payload.VisitId,
              Language: datasetLanguages[0].description,
              AvailableLanguages: datasetLanguages,
              Locale: "",
              TokenId: vitalsTokenResponse.token,
              Url: window.location.origin + "/api/vitals/",
            };
            setSyncSrc(`dcat:${JSON.stringify(data)}`);
            vitalsPoller.current.Start();
            setVitalsLaunched();
          });
        });
      } catch {
        toast.error("An error occurred launching VITALS");
      }
    },
    [currentUser, setVitalsLaunched]
  );

  const resumeVitalsTest = useCallback(
    async ({ datasetId, datasetLanguages, vitalsTestResultId: vtId }: ResumeVitalsTestParams) => {
      if (
        !currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) &&
        !currentUser.vitalsCertified
      ) {
        toast.error("Not certified to edit a VITALS test");
        return;
      }

      try {
        const vitalsTokenResponse = await VitalsAPI.vitalsGenerateToken(Number(datasetId));
        if (vitalsTokenResponse == null || vitalsTokenResponse.token == null) {
          throw new Error("unable to retrieve access token");
        }

        VitalsAPI.vitalsGetPayload(vtId!).then((payload: any) => {
          setDCATButtonClicked(true);
          const data = {
            Environment: EnvironmentType.NextGen,
            VisitIdNG: payload.WebId,
            VisitId: payload.VisitId,
            Language: datasetLanguages[0].description,
            AvailableLanguages: datasetLanguages,
            Locale: "",
            TokenId: vitalsTokenResponse.token,
            Url: window.location.origin + "/api/vitals/",
          };
          setSyncSrc(`dcat:${JSON.stringify(data)}`);
          vitalsPoller.current.Start();
          setVitalsLaunched();
        });
      } catch {
        toast.error("An error occurred launching VITALS");
      }
    },
    [currentUser, setVitalsLaunched]
  );

  const initCanSync = useCallback((user: any) => {
    if (
      user.permissions!.includes(AdminPermissions.GlobalAdmin) ||
      user.vitalsCertified
    ) {
      setCanSync(true);
    }
  }, []);

  return {
    vitalsLaunched,
    DCATButtonClicked,
    setDCATButtonClicked,
    DCATRunning,
    setDCATRunning,
    DCATerror,
    setDCATerror,
    canSync,
    setCanSync,
    syncSrc,
    setSyncSrc,
    showLaunchMessageVisible,
    vitalsTestResultId,
    setVitalsTestResultId,
    options,
    setVitalsLaunched,
    handleCustomProtocolError,
    startVitalsTest,
    resumeVitalsTest,
    initCanSync,
  };
}
